import { differenceInDays, getAge } from '@/shared/dateFormatUtils';
import { CalculationMethods } from '@/shared/models/priceCalculationMethods';
import _ from 'lodash';
import { AddOnServiceModel, ReservationAccUnitModel, ReservationAgencyFeeModel } from "./types";

export function getUnitOwnerIds(unitType: ReservationAccUnitModel){
  if (unitType.accomodationUnitId && unitType.ownerId) return [unitType.ownerId];

  const uniqueOwners = _.uniq(unitType.accomodationUnitOptions.filter(x => x.ownerId).map(x => x.ownerId as number));
  return uniqueOwners;
}

export function addOnServiceNeedsRange(serviceCalculationMethodId: number) {
  const rangeCalculationMethods = [
    CalculationMethods.PerNight,
    CalculationMethods.PerPersonPerNight,
    CalculationMethods.PerChildPerNight,
    CalculationMethods.PerAdultPerNight,
    CalculationMethods.PerDay
  ];

  return rangeCalculationMethods.includes(serviceCalculationMethodId);
}

export function addOnServiceDaysInPeriod(startDate: string, endDate: string, service: AddOnServiceModel) {
  if (
    !service.from &&
    !service.to &&
    (!service.periods || service.periods.length === 0)
  ) {
    const endingDate = new Date(endDate);
    if (service.calculationMethodId == CalculationMethods.PerDay) {
      endingDate.setDate(endingDate.getDate() + 1);
    }
    return differenceInDays(
      new Date(startDate),
      endingDate
    );
  }
  const currentDate = new Date(startDate.substring(0, 10));
  const departureDate = new Date(endDate.substring(0, 10));
  const servicePeriods: {
    from: Date;
    to: Date;
  }[] = [];



  if (service.from && service.to) {
    servicePeriods.push({
      from: new Date(service.from.substring(0, 10)),
      to: new Date(service.to.substring(0, 10)),
    });
  } else {
    servicePeriods.push(
      ...service.periods.map((x) => {
        return {
          from: new Date(x.from.substring(0, 10)),
          to: new Date(x.to.substring(0, 10)),
        };
      })
    );
  }

  let serviceDays = 0;

  if (service.isCityTax || service.calculationMethodId != CalculationMethods.PerDay) {
    while (currentDate.getTime() < departureDate.getTime()) {
      const dateInPeriod = servicePeriods.some(
        (x) =>
          x.from.getTime() <= currentDate.getTime() &&
          x.to.getTime() >= currentDate.getTime()
      );
      if (service.to && service.from && dateInPeriod) {
        serviceDays++;
      } else if (!service.to && !service.from && !dateInPeriod) {
        serviceDays++;
      }
      const originalTimezoneOffset = currentDate.getTimezoneOffset();
      currentDate.setDate(currentDate.getDate() + 1);
      const postAddTimezoneOffset = currentDate.getTimezoneOffset();

      if (originalTimezoneOffset !== postAddTimezoneOffset) {
        const timezoneOffsetDiff = originalTimezoneOffset - postAddTimezoneOffset;

        currentDate.setHours(currentDate.getHours() + (timezoneOffsetDiff / 60));
      }
    }
  }
  else {
    while (currentDate.getTime() <= departureDate.getTime()) {
      const dateInPeriod = servicePeriods.some(
        (x) =>
          x.from.getTime() <= currentDate.getTime() &&
          x.to.getTime() >= currentDate.getTime()
      );

      if (service.to && service.from && dateInPeriod) {
        serviceDays++;
      } else if (!service.to && !service.from && !dateInPeriod) {
        serviceDays++;
      }

      const originalTimezoneOffset = currentDate.getTimezoneOffset();
      currentDate.setDate(currentDate.getDate() + 1);
      const postAddTimezoneOffset = currentDate.getTimezoneOffset();

      if (originalTimezoneOffset !== postAddTimezoneOffset) {
        const timezoneOffsetDiff = originalTimezoneOffset - postAddTimezoneOffset;

        currentDate.setHours(currentDate.getHours() + (timezoneOffsetDiff / 60));
      }
    }
  }
  return serviceDays;
}

export function calculateAddOnServiceQuantity(
  service: AddOnServiceModel,
  unit: ReservationAccUnitModel,
  calculateWithEVisitorStayDates: boolean,
) {
  let adultNights = 0;
  let childNights = 0;

  let numberOfAdults = unit.numberOfAdults;
  let numberOfChildren = unit.numberOfChildren;
  let numberOfTaxableChildren = unit.childrenAges?.filter(x => x > 11 && x < 18).length ?? 0;

  if (calculateWithEVisitorStayDates && service.isCityTax && unit.guests.some((x) => x.isEVisitorCheckedIn)) {
    numberOfAdults = 0;
    numberOfChildren = 0;
    numberOfTaxableChildren = 0;

    unit.guests.filter((x) => x.isEVisitorCheckedIn).forEach((x) => {
      const age = x.dateOfBirth !== undefined
        ? getAge(x.dateOfBirth)
        : 18
      if (x.city !== unit.city) {
        const startIsOfTypeDate = !!x.eVisitorCheckInDate?.toISOString;
        const endIsOfTypeDate = !!x.eVisitorCheckOutDate?.toISOString;
        const startDate = x.eVisitorCheckInDate !== undefined && x.isEVisitorCheckedIn
          ? startIsOfTypeDate
            ? x.eVisitorCheckInDate.toISOString().substring(0, 10)
            : x.eVisitorCheckInDate.toString().substring(0, 10)
          : unit.arrivalDate.substring(0, 10);
        const endDate = x.eVisitorCheckOutDate !== undefined && x.isEVisitorCheckedIn
          ? endIsOfTypeDate
            ? x.eVisitorCheckOutDate.toISOString().substring(0, 10)
            : x.eVisitorCheckOutDate.toString().substring(0, 10)
          : x.checkOutDate?.toString().substring(0, 10) ?? unit.departureDate.substring(0, 10);
        if (age >= 18) {
          adultNights += addOnServiceDaysInPeriod(startDate, endDate, service);
        }
        else if (age > 11) {
          childNights += addOnServiceDaysInPeriod(startDate, endDate, service);
        }
      }
    })
  }

  let numberOfNights = 0;
  let numberOfDays = 0;

  if (service.isCityTax || service.calculationMethodId != CalculationMethods.PerDay) {
    numberOfNights = addOnServiceDaysInPeriod(unit.arrivalDate, unit.departureDate, service);
  }
  else {
    numberOfDays = addOnServiceDaysInPeriod(unit.arrivalDate, unit.departureDate, service);
  }

  if (addOnServiceNeedsRange(service.calculationMethodId) && service.serviceDateFrom && service.serviceDateTo && !service.isCityTax && !service.pricesAreSeasonal) {
    if (service.calculationMethodId == CalculationMethods.PerDay) {
      numberOfDays = addOnServiceDaysInPeriod(service.serviceDateFrom, service.serviceDateTo, service);
    }
    else {
      numberOfNights = addOnServiceDaysInPeriod(service.serviceDateFrom, service.serviceDateTo, service);
    }
  }

  if (service.isEditedManually && !service.isCityTax) {
    return;
  }

  switch (service.calculationMethodId) {
    case CalculationMethods.PerStay: {
      service.decimalQuantity = 1;
      break;
    }
    case CalculationMethods.PerNight: {
      service.decimalQuantity = numberOfNights;
      break;
    }
    case CalculationMethods.PerPersonPerNight: {
      service.decimalQuantity = numberOfNights * (numberOfAdults + numberOfChildren) + adultNights + childNights;
      break;
    }
    case CalculationMethods.PerPerson: {
      service.decimalQuantity = numberOfAdults + numberOfChildren;
      break;
    }
    case CalculationMethods.PerAdultPerson: {
      service.decimalQuantity = numberOfAdults;
      break;
    }
    case CalculationMethods.PerChildPerNight: {
      if (service.isCityTax) {
        service.decimalQuantity = numberOfNights * numberOfTaxableChildren + childNights;
      } else {
        service.decimalQuantity =
          numberOfNights * numberOfChildren
      }
      break;
    }
    case CalculationMethods.PerAdultPerNight: {
      service.decimalQuantity = numberOfNights * numberOfAdults + adultNights;
      break;
    }

    case CalculationMethods.PerDay: {
      service.decimalQuantity = numberOfDays
      break;
    }
  }
  if (service.defaultQuantity) {
    if (service.decimalQuantity < service.defaultQuantity) {
      service.decimalQuantity = service.defaultQuantity;
    }
  }
}

export function calculateAgencyFees( 
  propertyFee: ReservationAgencyFeeModel,
  unit: ReservationAccUnitModel
) {

    let totalPriceWithoutDiscount = 0;
    if (unit && unit.dayPrices) {
      totalPriceWithoutDiscount = unit.dayPrices.reduce(
        (sum, e) => sum + e.price, 0);
    }

    //this is the number used for relative agency fees calculation
    let totalPriceWithDiscount = totalPriceWithoutDiscount;
    if (unit && unit.discountPercent) {
      totalPriceWithDiscount = totalPriceWithDiscount * (1 - unit.discountPercent / 100)
    }

    const fee = {
      name: propertyFee.name,
      calculationMethodId: propertyFee.calculationMethodId,
      priceIsAbsolute: propertyFee.priceIsAbsolute,
      currencyId: propertyFee.currencyId,
      agencyFeeId: propertyFee.agencyFeeId,
      propertyId: propertyFee.propertyId
    } as ReservationAgencyFeeModel;

    switch(propertyFee.calculationMethodId) {

      case CalculationMethods.PerStay:
        if (propertyFee.priceIsAbsolute) {
              fee.amount = propertyFee.amount
            } else {
              fee.amount = totalPriceWithDiscount * (propertyFee.amount / 100);
            }
        break;

      case CalculationMethods.PerNight:
        if (propertyFee.priceIsAbsolute) {
              fee.amount = propertyFee.amount * unit.numberOfNights
            } else {
              fee.amount = totalPriceWithDiscount * (propertyFee.amount / 100) * unit.numberOfNights;
            }
        break;

      case CalculationMethods.PerPersonPerNight:
        if (propertyFee.priceIsAbsolute) {
              fee.amount = propertyFee.amount * unit.numberOfNights * (unit.numberOfAdults + unit.numberOfChildren)
            } else {
              fee.amount = totalPriceWithDiscount * (propertyFee.amount / 100) * unit.numberOfNights * (unit.numberOfAdults + unit.numberOfChildren);
            }
        break;
        
      case CalculationMethods.PerPerson:
        if (propertyFee.priceIsAbsolute) {
              fee.amount = propertyFee.amount * (unit.numberOfAdults + unit.numberOfChildren)
            } else {
              fee.amount = totalPriceWithDiscount * (propertyFee.amount / 100) * (unit.numberOfAdults + unit.numberOfChildren);
            }
        break;
      
      case CalculationMethods.PerAdultPerson:
        if (propertyFee.priceIsAbsolute) {
              fee.amount = propertyFee.amount * unit.numberOfAdults
            } else {
              fee.amount = totalPriceWithDiscount * (propertyFee.amount / 100) * unit.numberOfAdults
            }
        break;

      case CalculationMethods.PerAdultPerNight:
        if (propertyFee.priceIsAbsolute) {
              fee.amount = propertyFee.amount * unit.numberOfAdults * unit.numberOfNights
            } else {
              fee.amount = totalPriceWithDiscount * (propertyFee.amount / 100) * unit.numberOfAdults * unit.numberOfNights
            }
        break;

      case CalculationMethods.PerChildPerNight:
        if (propertyFee.priceIsAbsolute) {
              fee.amount = propertyFee.amount * unit.numberOfChildren * unit.numberOfNights
            } else {
              fee.amount = totalPriceWithDiscount * (propertyFee.amount / 100) * unit.numberOfChildren * unit.numberOfNights
            }
        break;

      case CalculationMethods.PerDay:
        if (propertyFee.priceIsAbsolute) {
             fee.amount = propertyFee.amount * (unit.numberOfNights + 1)
            } else {
              fee.amount = totalPriceWithDiscount * (propertyFee.amount / 100) * (unit.numberOfNights + 1)
            }
        break;

      case CalculationMethods.Manual:
        if (propertyFee.priceIsAbsolute) {
             fee.amount = propertyFee.amount
            } else {
              fee.amount = totalPriceWithDiscount * (propertyFee.amount / 100) 
            }
        break;
    }

    return fee;
  }

export type InvoiceRoutingReturnModel = {
  invoiceRoutingCustomerId: number;
  invoiceRoutingIsLegalEntity: boolean;
  invoiceRoutingName: string;
  invoiceRoutingOib: string;
  invoiceRoutingPaymentMeansId: number;
  invoiceRoutingCardTypeId: number;
  invoiceRoutingFinancialDocumentTypeId: number;
  priceMissmatchWithOfficialPrice: boolean;
}